import { create } from "zustand";
import { authorizeUser, updatePassword } from "../axios/login";
import { loginWithOneTimeCode } from "../axios/forgotPassword";
import useUserStore from "./userStore";
import { User } from "../types";
import { getAuthToken, setAuthToken } from "../utils/authToken";

type AuthState = {
  token: string | null;
  temporaryToken: string | null;
  temporaryUserName: string | null;
  userId: string | null;
  username: string | null;
  isAuthenticated: boolean;
  authError: string;
  passwordResettingLogin: string | null;
  login: (username: string, password: string) => Promise<void>;
  loginWithCode: (username: string, code: number) => Promise<void>;
  updatePassword: (newPassword: string) => Promise<void>;
  setAuthenticated: (auth: boolean) => void;
  setPasswordResettingLogin: (login: string) => void;
  clearPasswordResettingLogin: () => void;
};

export const useAuthStore = create<AuthState>((set, get) => ({
  token: getAuthToken(),
  temporaryToken: null,
  userId: null,
  username: null,
  isAuthenticated: !!getAuthToken(),
  authError: "",
  passwordResettingLogin: null,
  temporaryUserName: null,
  setAuthenticated: (auth) => set({ isAuthenticated: auth }),

  login: async (username: string, password: string) => {
    const { setToken, setUser } = useUserStore.getState();
    try {
      const response = await authorizeUser(username, password);
      if (response.valid) {
        if (
          response.message !== "Password confirmed" &&
          response.message !== "Password updated"
        ) {
          set({ temporaryToken: response.token, temporaryUserName: username });
          set({ authError: response.message || "Password issue" });
          throw new Error(response.message || "Password issue");
        }
        const token = response.token;
        const user: User | undefined = response.user;
        if (token) {
          setAuthToken(token);
          setToken(token);
        }
        if (user) {
          setUser(user);
          set({
            token,
            userId: user.id,
            username: user.username,
            isAuthenticated: true,
            authError: "",
          });
        }
      } else {
        set({ authError: response.message || "Invalid credentials" });
        throw new Error(response.message || "Invalid credentials");
      }
    } catch (error: any) {
      set({ authError: error.message });
      throw error;
    }
  },

  loginWithCode: async (username: string, code: number) => {
    const { setToken, setUser } = useUserStore.getState();
    try {
      const response = await loginWithOneTimeCode(username, code);
      if (!response.valid) {
        set({ authError: response.message || "Invalid code" });
        throw new Error(response.message || "Invalid code");
      }

      const token = response.token;
      const user: User | undefined = response.user;

      if (!token || !user) {
        set({ authError: "Invalid login response" });
        throw new Error("Invalid login response");
      }

      setAuthToken(token);
      setToken(token);

      setUser(user);
      set({
        token,
        userId: user.id,
        username: user.username,
        isAuthenticated: true,
        authError: "",
      });
    } catch (error: any) {
      set({ authError: error.message });
      throw error;
    }
  },

  updatePassword: async (newPassword: string): Promise<void> => {
    const { temporaryToken, temporaryUserName, login: loginUser } = get();
    if (!temporaryToken || !temporaryUserName) {
      throw new Error("No temporary credentials available");
    }
    try {
      await updatePassword(newPassword, temporaryToken);
      set({ temporaryToken: null, temporaryUserName: null });
      await loginUser(temporaryUserName, newPassword);
    } catch (error: any) {
      set({ authError: error.message });
      throw error;
    }
  },

  setPasswordResettingLogin: (login: string) => {
    set({ passwordResettingLogin: login });
    setTimeout(() => set({ passwordResettingLogin: null }), 10 * 60 * 1000);
  },

  clearPasswordResettingLogin: () => {
    set({ passwordResettingLogin: null });
  },
}));
